import dayjs from "dayjs";
import excel from "exceljs";
import type {
  childPartV2,
  partProcess,
  partOtherProcess,
  childPartV1,
} from "@/api/breakdownService";
import {
  CHILDPARTS_DATA_COLS,
  PARTPROCESS_DATA_COLS,
  PARTPROCESS_CENTER_COLS,
  PARTPROCESS_RIGHT_COLS,
  PARTOTHERPROCESS_DATA_COLS,
  PARTOTHERPROCESS_CENTER_COLS,
  PARTOTHERPROCESS_RIGHT_COLS,
} from "./constants";
import {
  triggerDownload,
  fillHeaderInfo,
  findInsertRow,
  spliceRowsPreservingMerges,
  applyMergesAndBorders,
  fillMaterialTypeSpecSheet,
  fillDimensionSheet,
} from "./sheetHelpers";
import {
  buildChildPartRows,
  buildPartProcessRows,
  buildPartOtherProcessRows,
  buildChildPartV1Rows,
  buildAllPartV1Rows,
} from "./rowBuilders";
import type { part } from "@/api/supplierService";
import { fetchAllSpecs, fetchDimensions } from "./cachedQueries";

export const exportPartToExcel = async ({
  partId,
  partNo,
  partName,
  ac,
  rt,
  oemPrice,
  sparePrice,
  supplierCode,
  supplierName,
  childPartsV2,
  partProcessV2,
  partOtherProcessV2,
}: {
  partId: number;
  partNo: string;
  partName: string;
  ac: string;
  rt: string;
  oemPrice: number;
  sparePrice: number;
  supplierCode: string;
  supplierName: string;
  childPartsV2: childPartV2[];
  partProcessV2: partProcess | undefined;
  partOtherProcessV2: partOtherProcess | undefined;
}) => {
  try {
    const fileName = `skc_ampa_cbd_part_${supplierCode}_${partNo}_${dayjs().format("YYYYMMDD_HHmmss")}`;
    const response = await fetch("/templates/template_export_part_v2.xlsx");
    const workbook = new excel.Workbook();
    await workbook.xlsx.load(await response.arrayBuffer());

    const sheet = workbook.getWorksheet("CBD");
    if (!sheet) throw new Error("Sheet 'CBD' not found");

    fillHeaderInfo(sheet, {
      "Supplier Name:": supplierName,
      "Date:": dayjs().format("YYYY-MM-DD"),
      "Part No.": partNo,
      "Part Name": partName,
      AC: ac,
      RT: rt,
    });

    // ── Child Parts ──────────────────────────────────────────────────────
    const childPartInsertRow = findInsertRow(sheet, "Material Cost", 4);
    if (childPartInsertRow === -1) {
      console.warn("Could not find 'Material Cost' header — check template");
      return;
    }
    const { rowsToInsert: childRows, mergeRequests: childMerges } =
      await buildChildPartRows(
        childPartsV2,
        childPartInsertRow,
        supplierCode,
        partId,
      );
    spliceRowsPreservingMerges(sheet, childPartInsertRow, childRows);
    applyMergesAndBorders(
      sheet,
      childMerges,
      childPartInsertRow,
      childRows.length,
      CHILDPARTS_DATA_COLS,
    );

    // ── OEM Part Process ─────────────────────────────────────────────────
    if (partProcessV2) {
      const oemProcessInsertRow = findInsertRow(sheet, "Part Process (OEM)", 2);
      if (oemProcessInsertRow !== -1) {
        const {
          rowsToInsert: oemProcessRows,
          mergeRequests: oemProcessMerges,
        } = buildPartProcessRows(
          partProcessV2.oem_part_process,
          partProcessV2.admin_oem_part_process,
          partProcessV2.profit_oem_part_process,
          partProcessV2.total_oem_part_process,
          oemProcessInsertRow,
        );
        spliceRowsPreservingMerges(sheet, oemProcessInsertRow, oemProcessRows);
        applyMergesAndBorders(
          sheet,
          oemProcessMerges,
          oemProcessInsertRow,
          oemProcessRows.length,
          PARTPROCESS_DATA_COLS,
          PARTPROCESS_CENTER_COLS,
          PARTPROCESS_RIGHT_COLS,
        );
      }
    }

    // ── Spare Part Process ───────────────────────────────────────────────
    if (partProcessV2) {
      const spareProcessInsertRow = findInsertRow(
        sheet,
        "Part Process (Spare)",
        2,
      );
      if (spareProcessInsertRow !== -1) {
        const {
          rowsToInsert: spareProcessRows,
          mergeRequests: spareProcessMerges,
        } = buildPartProcessRows(
          partProcessV2.spare_part_process,
          partProcessV2.admin_spare_part_process,
          partProcessV2.profit_spare_part_process,
          partProcessV2.total_spare_part_process,
          spareProcessInsertRow,
        );
        spliceRowsPreservingMerges(
          sheet,
          spareProcessInsertRow,
          spareProcessRows,
        );
        applyMergesAndBorders(
          sheet,
          spareProcessMerges,
          spareProcessInsertRow,
          spareProcessRows.length,
          PARTPROCESS_DATA_COLS,
          PARTPROCESS_CENTER_COLS,
          PARTPROCESS_RIGHT_COLS,
        );
      }
    }

    // ── OEM Part Other Process ───────────────────────────────────────────
    if (partOtherProcessV2) {
      const oemOtherProcessInsertRow = findInsertRow(
        sheet,
        "Part Other Process (OEM)",
        2,
      );
      if (oemOtherProcessInsertRow !== -1) {
        const {
          rowsToInsert: oemOtherProcessRows,
          mergeRequests: oemOtherProcessMerges,
        } = buildPartOtherProcessRows(
          partOtherProcessV2.oem_other_process,
          partOtherProcessV2.admin_oem_part_other_process,
          partOtherProcessV2.oem_profit_part_other_process,
          partOtherProcessV2.total_oem_other_part_process,
          oemOtherProcessInsertRow,
          oemPrice,
        );
        spliceRowsPreservingMerges(
          sheet,
          oemOtherProcessInsertRow,
          oemOtherProcessRows,
        );
        applyMergesAndBorders(
          sheet,
          oemOtherProcessMerges,
          oemOtherProcessInsertRow,
          oemOtherProcessRows.length,
          PARTOTHERPROCESS_DATA_COLS,
          PARTOTHERPROCESS_CENTER_COLS,
          PARTOTHERPROCESS_RIGHT_COLS,
        );
      }
    }

    // ── Spare Part Other Process ─────────────────────────────────────────
    if (partOtherProcessV2) {
      const spareOtherProcessInsertRow = findInsertRow(
        sheet,
        "Part Other Process (Spare)",
        2,
      );
      if (spareOtherProcessInsertRow !== -1) {
        const {
          rowsToInsert: spareOtherProcessRows,
          mergeRequests: spareOtherProcessMerges,
        } = buildPartOtherProcessRows(
          partOtherProcessV2.spare_other_process,
          partOtherProcessV2.admin_spare_part_other_process,
          partOtherProcessV2.spare_profit_part_other_process,
          partOtherProcessV2.total_spare_other_part_process,
          spareOtherProcessInsertRow,
          sparePrice,
        );
        spliceRowsPreservingMerges(
          sheet,
          spareOtherProcessInsertRow,
          spareOtherProcessRows,
        );
        applyMergesAndBorders(
          sheet,
          spareOtherProcessMerges,
          spareOtherProcessInsertRow,
          spareOtherProcessRows.length,
          PARTOTHERPROCESS_DATA_COLS,
          PARTOTHERPROCESS_CENTER_COLS,
          PARTOTHERPROCESS_RIGHT_COLS,
        );
      }
    }

    triggerDownload(await workbook.xlsx.writeBuffer(), fileName);
  } catch (error) {
    console.error("exportPartTemplateToExcel error:", error);
  }
};

export const exportPartTemplateToExcel = async () => {
  const response = await fetch("/templates/template_import_part_v2.xlsx");
  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());

  const specSheet = workbook.getWorksheet("Material Type&Spec");
  if (!specSheet) throw new Error("Sheet 'Material Type&Spec' not found");

  const dimensionSheet = workbook.getWorksheet("Dimension");
  if (!dimensionSheet) throw new Error("Sheet 'Dimension' not found");

  const allSpecs = await fetchAllSpecs();
  const sortedSpecs = allSpecs.sort(
    (a, b) =>
      a.material_type_name.localeCompare(b.material_type_name) ||
      a.spec_name.localeCompare(b.spec_name),
  );
  fillMaterialTypeSpecSheet(specSheet, sortedSpecs);

  const allDimensions = await fetchDimensions();
  const sortedDimensions = allDimensions.sort((a, b) => a.id - b.id);
  fillDimensionSheet(dimensionSheet, sortedDimensions);

  triggerDownload(await workbook.xlsx.writeBuffer(), "template_import_part_v2");
};

interface ExportPartV1ToExcelParams {
  supplierCode: string;
  supplierName: string;
  buyerCode: string;
  partId: number;
  partNo: string;
  partName: string;
  ac: string;
  rt: string;
  oemPrice: number;
  sparePrice: number;
  admin: string;
  profit: string;
  childParts: childPartV1[];
}

export const exportPartV1ToExcel = async ({
  supplierCode,
  supplierName,
  buyerCode,
  partId,
  partNo,
  partName,
  ac,
  rt,
  oemPrice,
  sparePrice,
  admin,
  profit,
  childParts,
}: ExportPartV1ToExcelParams) => {
  try {
    const fileName = `skc_ampa_cbd_part_${supplierCode}_${partNo}_${dayjs().format("YYYYMMDD_HHmmss")}`;
    const response = await fetch("/templates/template_export_part_v1.xlsx");
    const workbook = new excel.Workbook();
    await workbook.xlsx.load(await response.arrayBuffer());

    const sheet = workbook.getWorksheet("CBD");
    if (!sheet) throw new Error("Sheet 'CBD' not found");
    fillHeaderInfo(sheet, {
      "Supplier ID:": supplierCode,
      "Supplier Name:": supplierName,
      "Buyer ID:": buyerCode,
      "Part No.:": partNo,
      "Part Name:": partName,
      "OEM Price:": oemPrice.toString(),
      "Spare Price:": sparePrice.toString(),
      "A/C:": ac,
      "R/T:": rt,
      "Admin:": admin,
      "Profit:": profit,
    });

    const insertRow = findInsertRow(sheet, "CBD:", 2);
    if (insertRow === -1) {
      console.warn("Could not find 'CBD' header — check template");
      return;
    }

    const { rowsToInsert, colNumber } = await buildChildPartV1Rows({
      childPartsV1: childParts,
      supplierCode,
      partId,
      sheet,
      insertRow,
    });

    const CHILDPARTS_V1_DATA_COLS = Array.from(
      { length: colNumber },
      (_, i) => i + 1,
    );
    spliceRowsPreservingMerges(sheet, insertRow, rowsToInsert);
    applyMergesAndBorders(
      sheet,
      [],
      insertRow,
      rowsToInsert.length,
      CHILDPARTS_V1_DATA_COLS,
    );

    triggerDownload(await workbook.xlsx.writeBuffer(), fileName);
  } catch (error) {
    console.error("exportPartTemplateToExcel error:", error);
  }
};

export const exportPartV1TemplateToExcel = async () => {
  const response = await fetch("/templates/template_import_part_v1.xlsx");
  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());
  triggerDownload(await workbook.xlsx.writeBuffer(), "template_import_part_v1");
};

interface ExportAllPartV1ToExcelParams {
  supplierCode: string;
  supplierName: string;
  buyerCode: string;
  admin: string;
  profit: string;
  parts: part[];
  onProgress?: (current: number, total: number) => void;
}

export const exportAllPartV1ToExcel = async ({
  supplierCode,
  supplierName,
  buyerCode,
  admin,
  profit,
  parts,
  onProgress,
}: ExportAllPartV1ToExcelParams) => {
  try {
    const fileName = `AllPart_report_${dayjs().format("YYYYMMDD_HHmmss")}`;
    const response = await fetch("/templates/template_export_part_v1.xlsx");
    const workbook = new excel.Workbook();
    await workbook.xlsx.load(await response.arrayBuffer());

    const sheet = workbook.getWorksheet("CBD");
    if (!sheet) throw new Error("Sheet 'CBD' not found");

    // Remove the 6 part-level header rows (Part No.:, Part Name:, OEM Price:,
    // Spare Price:, A/C:, R/T:) — rows 4-9 — so Admin/Profit shift up. These
    // fields are moved into the table columns instead.
    sheet.spliceRows(4, 6);

    const insertRow = findInsertRow(sheet, "CBD:", 2);
    if (insertRow === -1) {
      console.warn("Could not find 'CBD' header — check template");
      return;
    }

    const { rowsToInsert, colNumber, separatorOffsets } =
      await buildAllPartV1Rows({
        parts,
        supplierCode,
        sheet,
        insertRow,
        onProgress,
      });

    // buildAllPartV1Rows splices in the leading columns, so fill the header
    // block afterwards (its labels have been restored to column 1).
    fillHeaderInfo(sheet, {
      "Supplier ID:": supplierCode,
      "Supplier Name:": supplierName,
      "Buyer ID:": buyerCode,
      "Admin:": admin,
      "Profit:": profit,
    });

    const ALLPARTS_V1_DATA_COLS = Array.from(
      { length: colNumber },
      (_, i) => i + 1,
    );
    spliceRowsPreservingMerges(sheet, insertRow, rowsToInsert);
    applyMergesAndBorders(
      sheet,
      [],
      insertRow,
      rowsToInsert.length,
      ALLPARTS_V1_DATA_COLS,
    );

    // Highlight the separator rows between parts in yellow.
    for (const offset of separatorOffsets) {
      const row = sheet.getRow(insertRow + offset);
      for (let c = 1; c <= colNumber; c++) {
        row.getCell(c).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFF00" },
        };
      }
    }

    triggerDownload(await workbook.xlsx.writeBuffer(), fileName);
  } catch (error) {
    console.error("exportAllPartV1ToExcel error:", error);
  }
};